import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { Button } from 'components/buttons';
import { DateToLocal } from 'components/dates';
import { FormTextArea } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { AlertConfirm } from 'components/notifications';
import './Note.css';

const defaultArray = [];

export function NotesThread(
    {
        id = null,
        notes = defaultArray,
        loading = false,
        maxLength = 255,
        saving = false,
        newNoteValue = "",
        onNewNoteChange,
        onAddNote,
        onEditNote,
        onDeleteNote
    }
) {

    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingText, setEditingText] = useState("");
    const [deletingNoteId, setDeletingNoteId] = useState(null);

    let addNoteInputId = null;
    if (id != null) {
        addNoteInputId = `${id}-add-note`;
    }

    function onNewNoteTextChange(event) {
        onNewNoteChange(event.target.value);
    }

    function onAddNoteClick() {
        onAddNote(newNoteValue);
    }

    function onEditClick(note) {
        setEditingNoteId(note.id);
        setEditingText(note.text);
    }

    function onEditingTextChange(event) {
        setEditingText(event.target.value);
    }

    function onEditCancelClick() {
        setEditingNoteId(null);
        setEditingText("");
    }

    function onEditSaveClick() {
        Promise.resolve(onEditNote(editingNoteId, editingText)).then(() => {
            setEditingNoteId(null);
            setEditingText("");
        }, () => {
            // Stay in edit mode with the user's text; the page surfaces the error
        });
    }

    function onDeleteClick(note) {
        setDeletingNoteId(note.id);
    }

    function onDeleteCancel() {
        setDeletingNoteId(null);
    }

    function onDeleteConfirm() {
        const noteId = deletingNoteId;
        setDeletingNoteId(null);
        onDeleteNote(noteId);
    }

    return (
        <div id={id}>
            <div className="spacer-bottom">
                <FormTextArea
                    id={addNoteInputId}
                    value={newNoteValue}
                    onChange={onNewNoteTextChange}
                    maxLength={maxLength}
                    showCounter
                    loading={loading}
                />
                <div className="button-row">
                    <Button
                        text="ADD NOTE"
                        intent="primary"
                        onClick={onAddNoteClick}
                        disabled={saving || newNoteValue.trim().length === 0}
                        loading={loading}
                    />
                </div>
            </div>
            <div className="notes-thread-list">
                {notes.map((note) => {

                    // The backend sets the modified date on insert, so an equal timestamp means the note was never edited
                    let showLastUpdated = false;
                    if (note.modifiedDate != null && !moment(note.modifiedDate).isSame(moment(note.createdDate))) {
                        showLastUpdated = true;
                    }

                    return (
                        <div className={classNames("notes-thread-item", { "bp6-skeleton": loading })} key={note.id}>
                            <ShowHide
                                evaluator={editingNoteId === note.id}
                                show={(
                                    <div className="inline-items">
                                        <div className="inline-item fill">
                                            <FormTextArea
                                                id={`note-${note.id}-edit-text`}
                                                value={editingText}
                                                onChange={onEditingTextChange}
                                                maxLength={maxLength}
                                                showCounter
                                                fill
                                            />
                                        </div>
                                        <Button
                                            id={`note-${note.id}-cancel-button`}
                                            iconOnly
                                            icon="cross"
                                            minimal
                                            large={false}
                                            onClick={onEditCancelClick}
                                            disabled={saving}
                                        />
                                        <Button
                                            id={`note-${note.id}-save-button`}
                                            iconOnly
                                            icon="floppy-disk"
                                            intent="primary"
                                            large={false}
                                            onClick={onEditSaveClick}
                                            disabled={saving || editingText.trim().length === 0}
                                        />
                                    </div>
                                )}
                                hide={(
                                    <>
                                        <p className="preserve-white-space">{note.text}</p>
                                        <div className="inline-items helper-text">
                                            <div className="inline-item">{note.author} on <DateToLocal format='Do MMMM YY HH:mm'>{note.createdDate}</DateToLocal></div>
                                            <ShowHide
                                                evaluator={showLastUpdated}
                                                show={(
                                                    <>
                                                        <div className='inline-item'>-</div>
                                                        <div className="inline-item">Last update <DateToLocal format='Do MMMM YY HH:mm'>{note.modifiedDate}</DateToLocal></div>
                                                    </>
                                                )}
                                            />
                                            <ShowHide
                                                evaluator={note.canEdit === true}
                                                show={(
                                                    <>
                                                        <Button
                                                            id={`note-${note.id}-edit-button`}
                                                            iconOnly
                                                            icon="edit"
                                                            minimal
                                                            large={false}
                                                            onClick={() => onEditClick(note)}
                                                            disabled={saving}
                                                        />
                                                        <Button
                                                            id={`note-${note.id}-delete-button`}
                                                            iconOnly
                                                            icon="trash"
                                                            minimal
                                                            intent="danger"
                                                            large={false}
                                                            onClick={() => onDeleteClick(note)}
                                                            disabled={saving}
                                                        />
                                                    </>
                                                )}
                                            />
                                        </div>
                                    </>
                                )}
                            />
                        </div>
                    );
                })}
            </div>
            <AlertConfirm
                isOpen={deletingNoteId != null}
                onConfirm={onDeleteConfirm}
                onCancel={onDeleteCancel}
                title="Confirm Delete"
                confirmButtonText="Delete"
                confirmButtonIntent="danger"
                saving={saving}
            >
                Are you sure you want to delete this note?
            </AlertConfirm>
        </div>
    );
}

NotesThread.propTypes = {
    id: PropTypes.string,
    notes: PropTypes.array,
    loading: PropTypes.bool,
    maxLength: PropTypes.number,
    saving: PropTypes.bool,
    newNoteValue: PropTypes.string,
    onNewNoteChange: PropTypes.func.isRequired,
    onAddNote: PropTypes.func.isRequired,
    onEditNote: PropTypes.func.isRequired,
    onDeleteNote: PropTypes.func.isRequired
};
